<template>
	<el-drawer
		:title="editingId ? '编辑商品' : '添加商品'"
		:visible.sync="visible"
		direction="rtl"
		size="92%"
		:append-to-body="true"
		:modal-append-to-body="true"
		:wrapper-closable="true"
		:destroy-on-close="true"
		custom-class="product-edit-drawer"
		@closed="onClosed">
		<div class="drawer-body">
			<el-form ref="goodsForm" :model="form" :rules="rules" label-width="108px" class="goods-form">
				<el-tabs v-model="activeTab" class="goods-form__tabs">
					<el-tab-pane label="基本信息" name="basic">
						<el-row :gutter="20">
							<el-col :span="12">
								<el-form-item label="名称" prop="name">
									<el-input v-model="form.name" placeholder="请输入名称" />
								</el-form-item>
							</el-col>
							<el-col :span="12">
								<el-form-item label="型号" prop="model">
									<el-input v-model="form.model" placeholder="请输入型号" />
								</el-form-item>
							</el-col>
							<el-col :span="12">
								<el-form-item label="分类" prop="category_id">
									<el-select v-model="form.category_id" filterable placeholder="选择分类" style="width:100%">
										<el-option
											v-for="c in categorySelectOptions"
											:key="c.id"
											:label="c.name"
											:value="c.id" />
									</el-select>
								</el-form-item>
							</el-col>
							<el-col :span="12">
								<el-form-item label="单位" prop="unit">
									<el-input v-model="form.unit" placeholder="请输入单位" />
								</el-form-item>
							</el-col>
							<el-col :span="12">
								<el-form-item label="计价方式">
									<el-select v-model="form.valuation" style="width:100%">
										<el-option v-for="(label, val) in valuationList" :key="val" :label="label" :value="Number(val)" />
									</el-select>
								</el-form-item>
							</el-col>
							<el-col :span="12">
								<el-form-item label="单价">
									<el-input-number v-model="form.price" :min="0" :precision="2" style="width:100%" />
								</el-form-item>
							</el-col>
							<el-col :span="24">
								<el-form-item label="工艺参数">
									<el-select v-model="form.parameter_id_list" multiple filterable collapse-tags placeholder="选择工艺/参数项" style="width:100%">
										<el-option v-for="p in parameterOptions" :key="p.id" :label="p.label || p.name" :value="p.id" />
									</el-select>
								</el-form-item>
							</el-col>
							<el-col :span="12">
								<el-form-item label="协同订单显示">
									<el-switch v-model="form.switch" active-text="开" inactive-text="关" />
								</el-form-item>
							</el-col>
							<el-col :span="12">
								<el-form-item label="标签开关">
									<el-switch v-model="form.tag_switch" active-text="开" inactive-text="关" />
								</el-form-item>
							</el-col>
							<el-col :span="12">
								<el-form-item label="加工尺寸">
									<el-switch v-model="form.add_size" active-text="开" inactive-text="关" />
								</el-form-item>
							</el-col>
							<el-col :span="12">
								<el-form-item label="状态">
									<el-select v-model="form.status" style="width:100%">
										<el-option label="上架" :value="true" />
										<el-option label="下架" :value="false" />
									</el-select>
								</el-form-item>
							</el-col>
							<el-col v-if="form.add_size" :span="24">
								<el-form-item label="切料参数">
									<div class="cutting-params-row">
										<span class="cutting-params-label">锯片厚度</span>
										<el-input
											v-model="form.saw_kerf"
											placeholder="3.5"
											class="cutting-params-input"
											@blur="onCuttingParamBlur('saw_kerf', 3.5)" />
										<span class="cutting-params-unit">mm</span>
										<span class="cutting-params-label cutting-params-label--gap">齐边距离</span>
										<el-input
											v-model="form.end_trim"
											placeholder="20"
											class="cutting-params-input"
											@blur="onCuttingParamBlur('end_trim', 20)" />
										<span class="cutting-params-unit">mm</span>
										<span class="cutting-params-hint">两头各扣；可用长 = 原料长 − 2×齐边</span>
									</div>
								</el-form-item>
							</el-col>
							<el-col :span="24">
								<el-form-item label="主图">
									<imageUpload v-model="form.image" :limit="1" />
								</el-form-item>
							</el-col>
							<el-col :span="24">
								<el-form-item label="相册">
									<div class="images-panel">
										<div v-for="(img, index) in form.images_list" :key="'img-'+index" class="images-panel__item">
											<imageUpload v-model="form.images_list[index]" :limit="1" />
											<el-button type="text" @click="removeGalleryImage(index)">移除</el-button>
										</div>
										<el-button type="primary" plain @click="addGalleryImage">添加图片</el-button>
									</div>
								</el-form-item>
							</el-col>
						</el-row>
					</el-tab-pane>

					<el-tab-pane label="规格定价" name="specs" lazy>
							<div class="sku-editor">
								<div class="specs-card__title">规格项</div>
								<div v-if="!skuList.length" class="sku-empty-hint">
									请先添加规格维度（如：型号、颜色），再为每个规格值填写价格。按住左侧图标可拖拽调整顺序。
								</div>
								<div ref="skuGroupsEl" class="sku-groups">
									<div v-for="(sku, k) in skuList" :key="sku.temp_id" class="sku-group">
										<div class="sku-group__main">
											<span class="sku-drag-handle el-icon-rank" title="按住拖动调整规格顺序"></span>
											<span class="sku-group__label">规格名称</span>
											<div class="sku-name-field" :style="{ width: skuNameInputWidth + 'px' }" @dblclick="startEditSkuName(k)">
												<el-input
													v-if="skuNameEditing === k"
													v-model="skuList[k].name"
													maxlength="5"
													placeholder="如：颜色"
													size="small"
													:class="'sku-name-edit-' + k"
													class="sku-option-input"
													@blur="finishSkuNameEdit"
													@keyup.enter.native="finishSkuNameEdit" />
												<span
													v-else
													class="sku-option-chip"
													:class="{ 'sku-option-chip--empty': !skuList[k].name }"
													:title="skuList[k].name || '双击编辑'">
													{{ skuList[k].name || '双击编辑' }}
												</span>
											</div>
											<el-button
												type="text"
												icon="el-icon-delete"
												title="删除规格"
												class="sku-action-btn sku-action-btn--danger"
												@click.stop="confirmDeleteMainSku(k)" />
										</div>
										<div class="sku-group__children">
											<span class="sku-group__label">规格值</span>
											<div class="sku-children-list" :data-group-temp-id="sku.temp_id">
												<div v-for="(child, c) in sku.children" :key="child.temp_id" class="sku-child">
													<span class="sku-child-drag-handle el-icon-rank" title="按住拖动调整规格值顺序"></span>
													<div class="sku-child__field" :style="{ width: skuChildWidth + 'px' }" @dblclick="startEditSkuValue(k, c)">
														<el-input
															v-if="isEditingSkuValue(k, c)"
															v-model="skuList[k].children[c].name"
															size="small"
															maxlength="22"
															placeholder="规格值"
															:class="'sku-value-edit-' + k + '-' + c"
															class="sku-option-input"
															@blur="finishSkuValueEdit"
															@keyup.enter.native="finishSkuValueEdit" />
														<span
															v-else
															class="sku-option-chip"
															:class="{ 'sku-option-chip--empty': !child.name }"
															:title="child.name || '双击编辑'">
															{{ child.name || '双击编辑' }}
														</span>
													</div>
													<el-button
														type="text"
														icon="el-icon-delete"
														title="删除"
														class="sku-action-btn sku-action-btn--danger"
														@click.stop="confirmDeleteChildrenSku(k, c)" />
												</div>
											</div>
											<el-button type="text" icon="el-icon-plus" class="sku-action-btn" @click="addChildrenSku(k)">添加规格值</el-button>
										</div>
									</div>
								</div>
								<el-button type="primary" plain icon="el-icon-plus" @click="addMainSku">添加规格</el-button>

								<div v-if="skuPrice.length" class="specs-card specs-card--sku-table">
									<div class="specs-card__head">
										<div class="specs-card__title">规格价格表（共 {{ skuPrice.length }} 行）</div>
									</div>
									<div class="specs-table-scroll">
										<el-table :data="skuPrice" stripe border size="small" class="specs-price-table sku-price-table">
											<el-table-column type="index" width="44" label="#" fixed="left" />
											<el-table-column v-for="(item, i) in skuDimensions" :key="item.temp_id" :label="item.name || ('规格'+(i+1))" :min-width="skuDimColWidth(item.name)" fixed="left">
												<template slot-scope="scope">
													<span class="sku-spec-val">{{ (scope.row.goods_sku_text[i] || '—') }}</span>
												</template>
											</el-table-column>
											<el-table-column label="图片" :width="skuImageColWidth" align="center" class-name="sku-table-image-col">
												<template slot-scope="scope">
													<imageUpload v-model="skuPrice[scope.$index].image" :limit="1" :cell-size="skuImageCellSize" compact />
												</template>
											</el-table-column>
											<el-table-column v-if="form.valuation !== 3" label="价格(元)" min-width="96" class-name="is-amount">
												<template slot-scope="scope">
													<el-input class="amount" v-model="skuPrice[scope.$index].price" size="small" @blur="onSkuPriceBlur(skuPrice[scope.$index])" />
												</template>
											</el-table-column>
											<template v-if="form.valuation === 3">
												<el-table-column v-for="col in specsPriceColumns" :key="'sku-'+col" :label="col" :min-width="colWidthInterval">
													<template slot-scope="scope">
														<el-input
															v-model="form.specs_price_list[scope.$index][colKey(col)]"
															size="small"
															class="interval-price-input"
															@focus="onIntervalPriceFocus(scope.$index, col)"
															@blur="onIntervalPriceBlur(scope.$index, col)" />
													</template>
												</el-table-column>
											</template>
											<el-table-column v-if="form.add_size" label="加工尺寸" :min-width="colWidthWeight">
												<template slot-scope="scope">
													<el-input
														v-model="scope.row.weight"
														size="small"
														class="weight-size-input"
														@focus="onWeightFocus(scope.row)"
														@blur="onWeightBlur(scope.row)" />
												</template>
											</el-table-column>
											<el-table-column v-if="form.add_size" :min-width="colWidthStockLen">
												<template slot="header">
													<div class="stock-len-header">
														<span>原料长度</span>
														<el-button
															type="text"
															class="stock-len-fill-all"
															:disabled="!skuPrice.length"
															@click.stop="fillAllStockLength">
															全部
														</el-button>
													</div>
												</template>
												<template slot-scope="scope">
													<el-input
														v-model="scope.row.stock_length"
														size="small"
														class="weight-size-input"
														@focus="onStockLengthFocus(scope.row)"
														@blur="onStockLengthBlur(scope.row)" />
												</template>
											</el-table-column>
											<el-table-column label="描述" :min-width="skuDescColWidth" class-name="sku-desc-col">
												<template slot-scope="scope">
													<el-tooltip
														:disabled="!descNeedsTooltip(scope.row.sn)"
														:content="scope.row.sn"
														placement="top-start"
														:open-delay="3000"
														popper-class="sku-desc-tooltip">
														<el-input
															v-model="scope.row.sn"
															type="textarea"
															:rows="3"
															resize="none"
															size="small"
															class="sku-desc-input"
															placeholder="描述" />
													</el-tooltip>
												</template>
											</el-table-column>
											<el-table-column v-if="isLightStripGoods" label="集成开关" width="96" align="center" fixed="right">
												<template slot-scope="scope">
													<el-switch
														v-model="scope.row.switch_enable"
														active-text="显"
														inactive-text="隐" />
												</template>
											</el-table-column>
											<el-table-column label="状态" width="88" fixed="right">
												<template slot-scope="scope">
													<el-switch
														:value="scope.row.status === 'up'"
														active-text="上"
														inactive-text="下"
														@change="(val) => setSkuStatus(scope.$index, val)" />
												</template>
											</el-table-column>
										</el-table>
									</div>
								</div>
							</div>
					</el-tab-pane>

					<el-tab-pane label="详情" name="content" lazy>
						<el-form-item label="摘要" prop="summary">
							<el-input v-model="form.summary" type="textarea" :rows="4" placeholder="商品摘要" />
						</el-form-item>
						<el-form-item label="详情" prop="content">
							<rich-text-editor v-model="form.content" height="360px" />
						</el-form-item>
						<el-form-item label="排序">
							<el-input-number v-model="form.sort" :min="0" style="width:240px" />
						</el-form-item>
					</el-tab-pane>
				</el-tabs>
			</el-form>
		</div>
		<div class="drawer-footer">
			<el-button @click="visible = false">取消</el-button>
			<el-button type="primary" :loading="saving" @click="submit">保存</el-button>
		</div>
	</el-drawer>
</template>

<script>
import Sortable from 'sortablejs';
import imageUpload from '@/components/ImageUpload/index.vue';
import RichTextEditor from '@/components/RichTextEditor/index.vue';
import {
	colKey,
	formatIntervalPrice,
	formatCurrencyDisplay,
	formatMmValue,
	formatMmDisplay,
	normalizeSpecsPriceList,
	defaultGoodsForm,
	buildSubmitPayload,
	fillFormFromDetail,
	initSkuFromDetail,
	childCategories,
	flattenDescendantCategories,
	resolveCategoryRootId,
	isLightStripRootId,
	isLightStripCategory,
	DEFAULT_SPECS_PRICE_COLUMNS,
} from '@/utils/goods-edit';

export default {
	name: 'ProductEditDrawer',
	components: { imageUpload, RichTextEditor },
	props: {
		categories: { type: Array, default: () => [] },
		valuationList: { type: Object, default: () => ({}) },
		parameterOptions: { type: Array, default: () => [] },
		specsPriceColumns: { type: Array, default: () => DEFAULT_SPECS_PRICE_COLUMNS },
		defaultRootId: { type: Number, default: 0 },
	},
	data() {
		return {
			visible: false,
			editingId: null,
			saving: false,
			activeTab: 'basic',
			/** 当前编辑上下文所属一级大类，分类下拉仅展示其子类 */
			scopedRootId: 0,
			form: defaultGoodsForm([]),
			skuList: [],
			skuPrice: [],
			countId: 1,
			isEditInit: false,
			skuImageCellSize: 104,
			skuImageColWidth: 152,
			colWidthModel: 102,
			colWidthDimDefault: 88,
			colWidthInterval: 70,
			colWidthWeight: 70,
			colWidthStockLen: 78,
			colWidthDescBase: 120,
			skuNameEditing: null,
			skuValueEditing: null,
			skuNameInputWidth: 128,
			skuChildWidth: 144,
			_mainSortable: null,
			_childSortables: [],
			_skuSortSignature: '',
			rules: {
				name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
				category_id: [{ required: true, message: '请选择分类', trigger: 'change' }],
				unit: [{ required: true, message: '请输入单位', trigger: 'blur' }],
				summary: [{ required: true, message: '请输入摘要', trigger: 'blur' }],
				content: [{ required: true, message: '请输入详情', trigger: 'blur' }],
			},
		};
	},
	computed: {
		isLightStripGoods() {
			return isLightStripCategory(this.categories, this.form.category_id);
		},
		/** 当前产品分类下的全部商品分类（含多级） */
		categorySelectOptions() {
			const rootId = Number(this.scopedRootId || this.defaultRootId || 0);
			if (!rootId) return [];
			const list = flattenDescendantCategories(this.categories, rootId);
			const selectedId = this.form && this.form.category_id;
			if (selectedId === '' || selectedId == null) return list;
			const exists = list.some((c) => String(c.id) === String(selectedId));
			if (exists) return list;
			const selected = (this.categories || []).find((c) => String(c.id) === String(selectedId));
			return selected ? [selected].concat(list) : list;
		},
		skuDimensions() {
			return this.skuList.filter((s) => s.children && s.children.length > 0);
		},
		skuDescColWidth() {
			let freed = this.form.add_size ? 88 - this.colWidthWeight : 88;
			/* 原料长度与加工尺寸同开同关 */
			freed += this.form.add_size ? 88 - this.colWidthStockLen : 88;
			if (this.form.valuation === 3) {
				freed += this.specsPriceColumns.length * (88 - this.colWidthInterval);
			}
			if (this.skuDimensions.some((d) => String(d.name || '').trim() === '型号')) {
				freed += 128 - this.colWidthModel;
			}
			return this.colWidthDescBase + freed;
		},
		skuSortSignature() {
			return this.skuList
				.map((s) => `${s.temp_id}:${(s.children || []).map((c) => c.temp_id).join(',')}`)
				.join('|');
		},
	},
	watch: {
		'form.valuation'(val) {
			if (val === 3) this.syncSpecsPriceWithSku();
		},
		skuPrice: {
			deep: true,
			handler() {
				if (this.form.valuation === 3) this.syncSpecsPriceWithSku();
			},
		},
		skuList: {
			deep: true,
			handler() {
				if (!this.isEditInit) return;
				this.buildSkuPriceTable();
			},
		},
		activeTab(val) {
			if (val === 'specs' && this.visible) this.scheduleInitSortables();
		},
		visible(val) {
			if (!val) this.destroySortables();
		},
		skuSortSignature() {
			if (this.visible && this.activeTab === 'specs') this.scheduleInitSortables();
		},
	},
	beforeDestroy() {
		this.destroySortables();
	},
	methods: {
		colKey,
		skuDimColWidth(name) {
			const label = String(name || '').trim();
			if (label === '型号') return this.colWidthModel;
			return this.colWidthDimDefault;
		},
		descNeedsTooltip(text) {
			const t = String(text || '').trim();
			if (!t) return false;
			if (t.split(/\r?\n/).length > 3) return true;
			return t.length > 72;
		},
		isEditingSkuValue(k, c) {
			return this.skuValueEditing === `${k}-${c}`;
		},
		focusSkuInput(className) {
			this.$nextTick(() => {
				const root = this.$el;
				if (!root) return;
				const input = root.querySelector(`${className} input`);
				if (input) input.focus();
			});
		},
		startEditSkuName(k) {
			this.skuNameEditing = k;
			this.skuValueEditing = null;
			this.focusSkuInput(`.sku-name-edit-${k}`);
		},
		finishSkuNameEdit() {
			this.skuNameEditing = null;
		},
		startEditSkuValue(k, c) {
			this.skuValueEditing = `${k}-${c}`;
			this.skuNameEditing = null;
			this.focusSkuInput(`.sku-value-edit-${k}-${c}`);
		},
		finishSkuValueEdit() {
			this.skuValueEditing = null;
		},
		skuConfirmOptions() {
			return {
				confirmButtonText: '删除',
				cancelButtonText: '取消',
				type: 'warning',
				customClass: 'product-edit-drawer-msgbox',
			};
		},
		confirmDeleteMainSku(k) {
			this.finishSkuNameEdit();
			this.finishSkuValueEdit();
			this.$confirm('确定删除该规格吗？', '提示', this.skuConfirmOptions())
				.then(() => { this.deleteMainSku(k); })
				.catch(() => {});
		},
		confirmDeleteChildrenSku(k, c) {
			this.finishSkuValueEdit();
			this.$confirm('确定删除该规格值吗？', '提示', this.skuConfirmOptions())
				.then(() => { this.deleteChildrenSku(k, c); })
				.catch(() => {});
		},
		defaultCategoryForRoot(rootId) {
			const children = childCategories(this.categories, rootId);
			return children.length ? children[0].id : defaultGoodsForm(this.categories).category_id;
		},
		openCreate(rootId) {
			this.editingId = null;
			this.resetForm();
			const pid = Number(rootId || this.defaultRootId || 0);
			this.scopedRootId = pid;
			if (pid) {
				this.form.category_id = this.defaultCategoryForRoot(pid);
				this.form.add_size = isLightStripRootId(pid);
			}
			this.decorateCuttingMmFields();
			if (!this.skuList.length) this.addMainSku();
			this.isEditInit = true;
			this.visible = true;
			this.scheduleInitSortables();
		},
		openEdit(id) {
			this.editingId = id;
			this.resetForm();
			this.scopedRootId = Number(this.defaultRootId || 0);
			this.loadDetail(id);
		},
		resetForm() {
			this.destroySortables();
			this.scopedRootId = Number(this.defaultRootId || 0);
			this.form = defaultGoodsForm(this.categories);
			this.form.is_sku = 1;
			this.skuList = [];
			this.skuPrice = [];
			this.countId = 1;
			this.isEditInit = false;
			this.skuNameEditing = null;
			this.skuValueEditing = null;
			this.activeTab = 'basic';
			this.$nextTick(() => {
				if (this.$refs.goodsForm) this.$refs.goodsForm.clearValidate();
			});
		},
		loadDetail(id) {
			const that = this;
			that.$fetch('addons/hlm/api.Goods/manageDetail', { id }).then(function(res) {
				if (res.code !== 1) return;
				that.applyDetail(res.data);
				that.visible = true;
				that.scheduleInitSortables();
			});
		},
		applyDetail(data) {
			this.form = fillFormFromDetail(data, this.categories);
			this.scopedRootId = resolveCategoryRootId(this.categories, this.form.category_id)
				|| Number(this.defaultRootId || 0);
			this.form.is_sku = 1;
			normalizeSpecsPriceList(this.form.specs_price_list);
			this.decorateSpecsPriceCurrency(this.form.specs_price_list);
			const countRef = { value: this.countId };
			const sku = initSkuFromDetail(data.skuList, data.skuPrice, countRef);
			this.countId = countRef.value;
			this.skuList = sku.skuList;
			this.skuPrice = sku.skuPrice;
			if (this.form.valuation === 3) this.syncSpecsPriceWithSku();
			this.decorateSkuWeightMm(this.skuPrice);
			this.decorateSkuStockLengthMm(this.skuPrice);
			this.decorateCuttingMmFields();
			if (!this.skuList.length) this.addMainSku();
			this.activeTab = 'specs';
			setTimeout(() => {
				this.isEditInit = true;
				this.scheduleInitSortables();
			}, 200);
		},
		addGalleryImage() { this.form.images_list.push(''); },
		removeGalleryImage(index) { this.form.images_list.splice(index, 1); },
		onSkuPriceBlur(row) { row.price = formatIntervalPrice(row.price); },
		onIntervalPriceFocus(index, col) {
			const row = this.form.specs_price_list[index];
			if (!row) return;
			const key = colKey(col);
			row[key] = formatIntervalPrice(row[key]);
		},
		onIntervalPriceBlur(index, col) {
			const row = this.form.specs_price_list[index];
			if (!row) return;
			const key = colKey(col);
			row[key] = formatCurrencyDisplay(row[key]);
		},
		decorateSpecsPriceCurrency(rows) {
			if (!rows || !rows.length) return;
			rows.forEach((row) => {
				['one', 'two', 'three', 'four', 'five'].forEach((key) => {
					if (row[key]) row[key] = formatCurrencyDisplay(row[key]);
				});
			});
		},
		onWeightFocus(row) {
			row.weight = formatMmValue(row.weight);
		},
		onWeightBlur(row) {
			row.weight = formatMmDisplay(row.weight);
		},
		onStockLengthFocus(row) {
			row.stock_length = formatMmValue(row.stock_length);
		},
		onStockLengthBlur(row) {
			const raw = formatMmValue(row.stock_length);
			row.stock_length = formatMmDisplay(raw === '' || raw == null ? 3000 : raw);
		},
		fillAllStockLength() {
			if (!this.skuPrice.length) return;
			const first = this.skuPrice.find((r) => r.stock_length != null && String(r.stock_length).trim() !== '');
			const def = formatMmValue(first ? first.stock_length : 3000) || '3000';
			const that = this;
			that.$prompt('将以下原料长度(mm)填入全部规格', '一键全部录入', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				inputValue: String(def),
				inputPattern: /^\d+(?:\.\d+)?(?:\s*mm)?$/i,
				inputErrorMessage: '请输入有效长度',
				closeOnClickModal: false,
			}).then(function(res) {
				const display = formatMmDisplay(res.value);
				that.skuPrice.forEach(function(row) {
					row.stock_length = display;
				});
				that.$message.success('已将原料长度同步到全部规格');
			}).catch(function() {});
		},
		onMmFieldFocus(field) {
			this.form[field] = formatMmValue(this.form[field]);
		},
		onMmFieldBlur(field, fallback) {
			const raw = formatMmValue(this.form[field]);
			const val = raw === '' || raw == null ? fallback : raw;
			this.form[field] = formatMmDisplay(val);
		},
		onCuttingParamBlur(field, fallback) {
			const raw = formatMmValue(this.form[field]);
			this.form[field] = raw === '' || raw == null ? fallback : raw;
		},
		decorateCuttingMmFields() {
			this.form.saw_kerf = formatMmValue(this.form.saw_kerf != null && this.form.saw_kerf !== '' ? this.form.saw_kerf : 3.5) || '3.5';
			this.form.end_trim = formatMmValue(this.form.end_trim != null && this.form.end_trim !== '' ? this.form.end_trim : 20) || '20';
		},
		decorateSkuWeightMm(rows) {
			if (!rows || !rows.length) return;
			rows.forEach((row) => {
				if (row.weight != null && row.weight !== '') {
					row.weight = formatMmDisplay(row.weight);
				}
			});
		},
		decorateSkuStockLengthMm(rows) {
			if (!rows || !rows.length) return;
			rows.forEach((row) => {
				const v = row.stock_length != null && row.stock_length !== '' ? row.stock_length : 3000;
				row.stock_length = formatMmDisplay(v);
			});
		},
		setSkuStatus(index, val) {
			this.skuPrice[index].status = val ? 'up' : 'down';
		},
		scheduleInitSortables() {
			this.$nextTick(() => {
				this.initSortables();
			});
		},
		destroySortables() {
			if (this._mainSortable) {
				this._mainSortable.destroy();
				this._mainSortable = null;
			}
			(this._childSortables || []).forEach((s) => {
				try { s.destroy(); } catch (e) { /* ignore */ }
			});
			this._childSortables = [];
			this._skuSortSignature = '';
		},
		initSortables() {
			if (!this.visible || this.activeTab !== 'specs') return;
			const groupsEl = this.$refs.skuGroupsEl;
			if (!groupsEl) return;
			const signature = this.skuSortSignature;
			if (signature === this._skuSortSignature && this._mainSortable) return;
			this.destroySortables();
			this._skuSortSignature = signature;
			this._mainSortable = Sortable.create(groupsEl, {
				animation: 160,
				handle: '.sku-drag-handle',
				draggable: '.sku-group',
				ghostClass: 'sku-group--ghost',
				chosenClass: 'sku-group--chosen',
				dragClass: 'sku-group--drag',
				onEnd: (evt) => this.onMainSkuSortEnd(evt),
			});
			this._childSortables = [];
			groupsEl.querySelectorAll('.sku-children-list').forEach((el) => {
				const sortable = Sortable.create(el, {
					animation: 160,
					handle: '.sku-child-drag-handle',
					draggable: '.sku-child',
					ghostClass: 'sku-child--ghost',
					chosenClass: 'sku-child--chosen',
					dragClass: 'sku-child--drag',
					onEnd: (evt) => this.onChildSkuSortEnd(evt),
				});
				this._childSortables.push(sortable);
			});
		},
		onMainSkuSortEnd(evt) {
			const { oldIndex, newIndex } = evt;
			if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
			this.finishSkuNameEdit();
			this.finishSkuValueEdit();
			const moved = this.skuList.splice(oldIndex, 1)[0];
			this.skuList.splice(newIndex, 0, moved);
			this.buildSkuPriceTable();
			this.scheduleInitSortables();
		},
		onChildSkuSortEnd(evt) {
			const { oldIndex, newIndex } = evt;
			if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
			const tempId = Number(evt.from && evt.from.dataset ? evt.from.dataset.groupTempId : 0);
			const groupIndex = this.skuList.findIndex((s) => s.temp_id === tempId);
			if (groupIndex < 0) return;
			this.finishSkuValueEdit();
			const children = this.skuList[groupIndex].children;
			const moved = children.splice(oldIndex, 1)[0];
			children.splice(newIndex, 0, moved);
			this.buildSkuPriceTable();
			this.scheduleInitSortables();
		},
		addMainSku() {
			this.skuList.push({ id: 0, temp_id: this.countId++, name: '', pid: 0, children: [] });
			const k = this.skuList.length - 1;
			this.buildSkuPriceTable();
			this.startEditSkuName(k);
			this.scheduleInitSortables();
		},
		addChildrenSku(k) {
			const group = this.skuList[k];
			if (group.children.some((c) => !String(c.name).trim())) return;
			if (!group.children.length) this.skuPrice = [];
			group.children.push({ id: 0, temp_id: this.countId++, name: '', pid: group.id, children: [] });
			const c = group.children.length - 1;
			this.buildSkuPriceTable();
			this.startEditSkuValue(k, c);
			this.scheduleInitSortables();
		},
		deleteMainSku(k) {
			const hadChildren = this.skuList[k].children.length > 0;
			this.skuList.splice(k, 1);
			if (this.skuNameEditing === k) this.skuNameEditing = null;
			else if (this.skuNameEditing != null && this.skuNameEditing > k) this.skuNameEditing -= 1;
			if (this.skuValueEditing != null) {
				const parts = String(this.skuValueEditing).split('-');
				const editK = Number(parts[0]);
				if (editK === k) this.skuValueEditing = null;
				else if (editK > k) this.skuValueEditing = `${editK - 1}-${parts[1]}`;
			}
			if (hadChildren) this.skuPrice = [];
			this.buildSkuPriceTable();
			this.scheduleInitSortables();
		},
		deleteChildrenSku(k, c) {
			const name = this.skuList[k].children[c].name;
			this.skuList[k].children.splice(c, 1);
			if (this.skuValueEditing === `${k}-${c}`) this.skuValueEditing = null;
			else if (this.skuValueEditing != null) {
				const parts = String(this.skuValueEditing).split('-');
				const editK = Number(parts[0]);
				const editC = Number(parts[1]);
				if (editK === k && editC > c) this.skuValueEditing = `${k}-${editC - 1}`;
			}
			this.skuPrice = this.skuPrice.filter((row) => !(row.goods_sku_text || []).includes(name));
			if (!this.skuList[k].children.length) this.skuPrice = [];
			this.buildSkuPriceTable();
			this.scheduleInitSortables();
		},
		skuComboTitle(row, index) {
			const text = (row.goods_sku_text || []).filter(Boolean).join(' / ');
			return text || ('组合' + (index + 1));
		},
		syncSpecsPriceWithSku() {
			if (this.form.valuation !== 3) return;
			const prevByTitle = new Map(this.form.specs_price_list.map((row) => [row.title, row]));
			this.form.specs_price_list = this.skuPrice.map((sku, index) => {
				const title = this.skuComboTitle(sku, index);
				const existing = prevByTitle.get(title) || this.form.specs_price_list[index];
				return {
					title,
					one: existing && existing.one ? existing.one : '',
					two: existing && existing.two ? existing.two : '',
					three: existing && existing.three ? existing.three : '',
					four: existing && existing.four ? existing.four : '',
					five: existing && existing.five ? existing.five : '',
				};
			});
		},
		buildSkuPriceTable() {
			const arr = [];
			const nameByTempId = new Map();
			this.skuList.forEach((s1) => {
				if (!s1.children.length) return;
				arr.push(s1.children.map((s2) => s2.temp_id));
				s1.children.forEach((child) => {
					nameByTempId.set(child.temp_id, child.name);
				});
			});
			if (!arr.length) {
				this.skuPrice = [];
				if (this.form.valuation === 3) this.form.specs_price_list = [];
				return;
			}
			const prevByKey = new Map(
				this.skuPrice.map((row) => [(row.goods_sku_temp_ids || []).join(','), row]),
			);
			const next = [];
			this.collectSkuCombos(arr, 0, [], (tempDetailIds) => {
				const key = tempDetailIds.join(',');
				const tempDetail = tempDetailIds.map((id) => nameByTempId.get(id) || '');
				const existing = prevByKey.get(key);
				if (existing) {
					existing.goods_sku_text = tempDetail;
					existing.goods_sku_temp_ids = tempDetailIds.slice();
					existing.weigh = next.length + 1;
					if (existing.switch_enable === undefined) {
						existing.switch_enable = false;
					}
					next.push(existing);
				} else {
					next.push({
						id: 0,
						temp_id: next.length + 1,
						goods_sku_ids: '',
						goods_sku_temp_ids: tempDetailIds.slice(),
						goods_sku_text: tempDetail,
						image: '',
						price: 0,
						weight: 0,
						stock_length: formatMmDisplay(3000),
						sn: '',
						status: 'up',
						switch_enable: false,
						weigh: next.length + 1,
					});
				}
			});
			this.skuPrice = next;
			this.syncSpecsPriceWithSku();
		},
		collectSkuCombos(arr, k, temp, onCombo) {
			if (k === arr.length) {
				if (k !== 0) onCombo(temp.slice());
				return;
			}
			for (let i = 0; i < arr[k].length; i++) {
				temp[k] = arr[k][i];
				this.collectSkuCombos(arr, k + 1, temp, onCombo);
			}
		},
		submit() {
			const that = this;
			that.$refs.goodsForm.validate(function(valid) {
				if (!valid) return;
				if (that.form.valuation === 3) {
					normalizeSpecsPriceList(that.form.specs_price_list);
				}
				that.saving = true;
				that.form.is_sku = 1;
				const payload = buildSubmitPayload(that.form, that.skuList, that.skuPrice);
				that.$post('addons/hlm/api.Goods/manageSave', payload).then(function(res) {
					if (res.code === 1) {
						that.$message.success(res.msg || '保存成功');
						that.visible = false;
						that.$emit('saved');
					}
				}).finally(function() {
					that.saving = false;
				});
			});
		},
		onClosed() {
			this.editingId = null;
		},
	},
};
</script>

<style scoped>
.drawer-body {
	height: calc(100% - 56px);
	overflow: auto;
	padding: 0 16px 16px;
	box-sizing: border-box;
	min-width: 0;
}
.goods-form {
	min-width: 0;
}
.goods-form__tabs >>> .el-tabs__content {
	overflow: visible;
}
.drawer-footer {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	padding: 12px 20px;
	border-top: 1px solid #ebeef5;
	background: #fff;
	text-align: right;
	z-index: 1;
}
.goods-form__tabs >>> .el-tabs__content {
	padding-top: 12px;
}
.images-panel {
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
	align-items: flex-start;
}
.images-panel__item {
	display: flex;
	flex-direction: column;
	gap: 4px;
}
.preview-img {
	max-width: 120px;
	max-height: 120px;
	object-fit: contain;
}
.preview-img--sm {
	max-width: 80px;
	max-height: 80px;
}
.specs-card {
	margin-top: 8px;
	border: 1px solid #ebeef5;
	border-radius: 6px;
	overflow: visible;
}
.specs-card--sku-table {
	margin-top: 16px;
	overflow: hidden;
}
.specs-table-scroll {
	overflow-x: auto;
	max-width: 100%;
	-webkit-overflow-scrolling: touch;
}
.specs-price-table {
	width: 100%;
	min-width: max-content;
}
.specs-price-table >>> .el-table__header th {
	background-color: #f5f7fa !important;
	font-weight: 600;
	color: #374151;
	font-size: 14px;
}
.specs-price-table >>> .el-table__header th .cell {
	font-weight: 600;
	color: #374151;
}
.specs-price-table >>> .el-table__body td.el-table__cell {
	vertical-align: middle;
}
.specs-price-table >>> .el-table__body tr.el-table__row--striped td {
	background: #f9fafb;
}
.specs-price-table >>> .el-table__body tr:hover > td {
	background: #ecf8f5 !important;
}
.specs-price-table >>> .sku-table-image-col {
	overflow: visible;
}
.specs-price-table >>> .sku-table-image-col .cell {
	overflow: visible;
	padding: 10px 14px 8px 10px;
	line-height: 1;
}
.specs-price-table >>> th.sku-table-image-col .cell {
	padding: 10px 14px 8px 10px;
}
.interval-price-input >>> .el-input__inner {
	text-align: center;
	padding: 0 4px;
	font-variant-numeric: tabular-nums;
	font-size: 13px;
}
.field-hint {
	margin-top: 4px;
	font-size: 12px;
	line-height: 1.4;
	color: #909399;
}
.cutting-params-row {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px 8px;
	min-height: 32px;
}
.cutting-params-label {
	color: #606266;
	font-size: 13px;
	white-space: nowrap;
}
.cutting-params-label--gap {
	margin-left: 12px;
}
.cutting-params-input {
	width: 72px;
}
.cutting-params-input >>> .el-input__inner {
	height: 28px;
	line-height: 28px;
	padding: 0 8px;
	text-align: center;
	font-variant-numeric: tabular-nums;
}
.cutting-params-unit {
	color: #909399;
	font-size: 12px;
}
.cutting-params-hint {
	margin-left: 8px;
	color: #909399;
	font-size: 12px;
	line-height: 1.3;
}
.stock-len-header {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-wrap: wrap;
	gap: 2px 6px;
	line-height: 1.2;
}
.stock-len-fill-all {
	padding: 0;
	font-size: 12px;
	color: #1e6c99;
}
.stock-len-fill-all:hover,
.stock-len-fill-all:focus {
	color: #155a82;
}
.weight-size-input >>> .el-input__inner {
	text-align: center;
	padding: 0 4px;
	font-variant-numeric: tabular-nums;
	font-size: 13px;
}
.specs-price-table >>> td.sku-desc-col {
	vertical-align: top;
}
.specs-price-table >>> td.sku-desc-col .cell {
	padding: 8px 6px !important;
	overflow: visible;
	white-space: normal;
	line-height: 1.5;
}
.sku-desc-input >>> .el-textarea__inner {
	font-size: 13px;
	line-height: 1.5;
	padding: 6px 8px;
	min-height: calc(1.5em * 3 + 12px);
	resize: none;
}
.sku-spec-val {
	display: inline-block;
	max-width: 100%;
	padding: 2px 6px;
	border-radius: 4px;
	background: #f5f7fa;
	font-size: 13px;
	line-height: 1.4;
	word-break: break-word;
}
.specs-card__head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 16px;
	background: #f5f7fa;
	border-bottom: 1px solid #ebeef5;
}
.specs-card__title {
	font-weight: 600;
	font-size: 15px;
}
.specs-card__sub {
	margin-top: 4px;
	font-size: 13px;
	color: #909399;
}
.specs-empty {
	padding: 24px;
	text-align: center;
	color: #909399;
	font-size: 14px;
}
.sku-groups {
	display: flex;
	flex-direction: column;
	gap: 0;
}
.sku-group {
	margin-bottom: 12px;
	padding: 12px;
	border: 1px dashed #dcdfe6;
	border-radius: 6px;
	background: #fff;
	transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.sku-group--ghost {
	opacity: 0.45;
	background: #f0faf7;
	border-color: #45b299;
}
.sku-group--chosen {
	border-color: #45b299;
	box-shadow: 0 2px 10px rgba(69, 178, 153, 0.18);
}
.sku-group--drag {
	opacity: 1;
}
.sku-group__label {
	flex-shrink: 0;
	font-size: 14px;
	color: #909399;
}
.sku-group__main,
.sku-group__children {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	margin-bottom: 8px;
}
.sku-group__children {
	margin-bottom: 0;
}
.sku-drag-handle,
.sku-child-drag-handle {
	flex-shrink: 0;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	color: #c0c4cc;
	cursor: grab;
	border-radius: 4px;
	user-select: none;
	font-size: 16px;
}
.sku-drag-handle:hover,
.sku-child-drag-handle:hover {
	color: #45b299;
	background: rgba(69, 178, 153, 0.1);
}
.sku-drag-handle:active,
.sku-child-drag-handle:active {
	cursor: grabbing;
}
.sku-name-field {
	flex-shrink: 0;
}
.sku-children-list {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
}
.sku-child {
	display: flex;
	align-items: center;
	gap: 2px;
	flex-shrink: 0;
	background: #fff;
	border-radius: 4px;
}
.sku-child--ghost {
	opacity: 0.4;
}
.sku-child--chosen {
	box-shadow: 0 1px 6px rgba(69, 178, 153, 0.2);
}
.sku-child--drag {
	opacity: 1;
}
.sku-child__field {
	flex: 1;
	min-width: 0;
}
.sku-option-chip {
	display: block;
	width: 100%;
	height: 32px;
	line-height: 30px;
	padding: 0 10px;
	border: 1px solid #dcdfe6;
	border-radius: 4px;
	background: #fff;
	font-size: 14px;
	color: #606266;
	cursor: pointer;
	user-select: none;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	box-sizing: border-box;
}
.sku-option-chip:hover {
	border-color: #45b299;
	color: #45b299;
	background: #f6fffd;
}
.sku-option-chip--empty {
	color: #c0c4cc;
}
.sku-option-input >>> .el-input__inner {
	height: 32px;
	line-height: 32px;
}
.sku-action-btn {
	padding: 4px !important;
	margin: 0 !important;
	font-size: 17px;
	color: #45b299;
}
.sku-action-btn:hover {
	color: #3a9a82;
	background: rgba(69, 178, 153, 0.08);
	border-radius: 4px;
}
.sku-action-btn--danger {
	color: #ef4444;
}
.sku-action-btn--danger:hover {
	color: #dc2626;
	background: #fef2f2;
}
.sku-editor > .el-button--primary.is-plain {
	margin-top: 4px;
}
.sku-empty-hint {
	margin: 8px 0 12px;
	padding: 10px 12px;
	border-radius: 6px;
	background: #f4f4f5;
	color: #909399;
	font-size: 14px;
	line-height: 1.5;
}
</style>

<style>
.product-edit-drawer {
	z-index: 3000 !important;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}
.product-edit-drawer .el-drawer__header {
	margin-bottom: 0;
	padding: 18px 24px;
	border-bottom: 1px solid #e5e7eb;
}
.product-edit-drawer .el-drawer__header span {
	font-size: 18px;
	font-weight: 600;
	color: #1f2937;
	letter-spacing: -0.01em;
}
.product-edit-drawer .el-drawer__close-btn {
	font-size: 19px;
	color: #9ca3af;
}
.product-edit-drawer .el-drawer__body {
	position: relative;
	padding-bottom: 64px;
	height: 100%;
	box-sizing: border-box;
	overflow: hidden;
}
.product-edit-drawer .drawer-body {
	max-width: 100%;
}
.product-edit-drawer .specs-table-scroll {
	scrollbar-width: thin;
}
.product-edit-drawer .specs-table-scroll::-webkit-scrollbar {
	height: 6px;
}
.product-edit-drawer .specs-table-scroll::-webkit-scrollbar-thumb {
	background: #d1d5db;
	border-radius: 3px;
}
.sku-desc-tooltip {
	max-width: 420px !important;
	line-height: 1.6 !important;
	font-size: 14px !important;
	white-space: pre-wrap !important;
	word-break: break-word !important;
}
.product-edit-drawer .el-form-item__label {
	font-size: 14px;
	font-weight: 500;
	color: #374151;
}
.product-edit-drawer .el-input__inner,
.product-edit-drawer .el-textarea__inner {
	font-size: 14px;
	border-radius: 6px;
}
.product-edit-drawer .el-input.is-focus .el-input__inner,
.product-edit-drawer .el-input__inner:focus,
.product-edit-drawer .el-textarea__inner:focus {
	border-color: #ef4444;
}
.product-edit-drawer .el-input-number.is-active .el-input__inner,
.product-edit-drawer .el-input-number .el-input__inner:focus {
	border-color: #ef4444;
}
.product-edit-drawer .goods-form__tabs .el-tabs__item {
	font-size: 15px;
	font-weight: 500;
	height: 42px;
	line-height: 42px;
}
.product-edit-drawer .goods-form__tabs .el-tabs__item.is-active {
	color: #45b299;
	font-weight: 600;
}
.product-edit-drawer .goods-form__tabs .el-tabs__active-bar {
	background-color: #45b299;
}
.product-edit-drawer .el-button {
	font-size: 14px;
}
.product-edit-drawer .el-button--primary {
	background-color: #45b299;
	border-color: #45b299;
	border-radius: 6px;
	font-size: 14px;
}
.product-edit-drawer .el-select-dropdown__item,
.product-edit-drawer .el-switch__label {
	font-size: 14px;
}
.product-edit-drawer .el-table,
.product-edit-drawer .el-table .cell {
	font-size: 14px;
}
.product-edit-drawer .el-button--primary:hover,
.product-edit-drawer .el-button--primary:focus {
	background-color: #3a9a82;
	border-color: #3a9a82;
}
/* $confirm 弹层需高于 drawer (z-index: 3000) */
body > .el-message-box__wrapper {
	z-index: 4100 !important;
}
</style>
